import { useAtomValue, useSetAtom, useStore } from 'jotai';
import { FirebaseError } from 'firebase/app';

import { AI_CREDITS_EXHAUSTED } from '@flintmint/core-shared/constants';

import { capture } from '@/analytics';
import { useServices } from '@/providers/services-provider';
import { pendingUserSourceResyncsAtom, userSourceProcessingFailuresAtom, type UserSourceProcessingFailure } from '@/state';

import type { UserSource } from '@flintmint/core-shared/rxdb';

export const useResyncUserSource = (userSource?: Pick<UserSource, 'id' | 'syncStatus'>) => {
  const resyncById = useResyncUserSourceById();
  const pendingResyncs = useAtomValue(pendingUserSourceResyncsAtom);

  const isResyncing = userSource?.syncStatus === 'processing' || (!!userSource && !!pendingResyncs[userSource.id]);

  const runResync = (newUrl?: string) => {
    if (!userSource || isResyncing) {
      return Promise.resolve();
    }
    return resyncById(userSource.id, newUrl);
  };

  return {
    isResyncing,
    resync: () => runResync().catch(() => {}),
    resyncWithUrl: (newUrl: string) => runResync(newUrl),
  };
};

/**
 * Resyncs a source by id. A failure or an AI Energy refusal is reported to `UserSourceRefusalWatcher`, which shows
 * it, and the promise still rejects with it.
 */
export const useResyncUserSourceById = () => {
  const { callableApi } = useServices();
  const { isResyncPending, setPendingResync, clearPendingResync } = useUserSourceResyncPending();
  const setFailures = useSetAtom(userSourceProcessingFailuresAtom);

  const reportFailure = (failure: UserSourceProcessingFailure) => {
    setFailures((failures) => [...failures, failure]);
  };

  return (userSourceId: string, newUrl?: string) => {
    if (isResyncPending(userSourceId)) {
      return Promise.resolve();
    }

    setPendingResync(userSourceId);
    const request = callableApi
      ? callableApi.resyncUserSource(userSourceId, newUrl ? { url: newUrl } : undefined)
      : Promise.reject(new Error('Resync is unavailable until the connection to the server is established'));
    return request
      .then(
        () => {
          capture('user_source_resynced', { has_new_url: !!newUrl });
        },
        (error: unknown) => {
          reportResyncFailure(error, userSourceId, reportFailure);
          throw error;
        }
      )
      .finally(() => {
        clearPendingResync(userSourceId);
      });
  };
};

const useUserSourceResyncPending = () => {
  // Read through the store so two calls in the same render cannot both see the source as idle.
  const store = useStore();
  const setPendingResyncs = useSetAtom(pendingUserSourceResyncsAtom);

  return {
    isResyncPending: (userSourceId: string) => !!store.get(pendingUserSourceResyncsAtom)[userSourceId],
    setPendingResync: (userSourceId: string) => {
      setPendingResyncs((pendingResyncs) => ({
        ...pendingResyncs,
        [userSourceId]: true,
      }));
    },
    clearPendingResync: (userSourceId: string) => {
      setPendingResyncs((pendingResyncs) => {
        const nextPendingResyncs = { ...pendingResyncs };
        delete nextPendingResyncs[userSourceId];
        return nextPendingResyncs;
      });
    },
  };
};

/**
 * Reports a failed `resyncUserSource` call. Only a failed processing run is worth resyncing again; a Blueprint deleted
 * meanwhile reports nothing.
 */
const reportResyncFailure = (
  error: unknown,
  userSourceId: string,
  reportFailure: (failure: UserSourceProcessingFailure) => void
) => {
  if (isAiCreditsExhausted(error)) {
    capture('ai_credits_exhausted', { source: 'user_source_resync' });
    reportFailure({ userSourceId, error: AI_CREDITS_EXHAUSTED, retryable: true });
    return;
  }
  if (isFunctionsError(error, 'not-found')) {
    return;
  }

  console.error('resync failed', error);
  reportFailure({ userSourceId, error: undefined, retryable: isFunctionsError(error, 'internal') });
};

const isFunctionsError = (error: unknown, code: string) => error instanceof FirebaseError && error.code === `functions/${code}`;

// The Firebase functions SDK appends the HTTP status to the message (`ai_credits_exhausted [400]`).
const aiCreditsExhaustedMessage = new RegExp(`^${AI_CREDITS_EXHAUSTED}( \\[\\d+\\])?$`);

/** The `resyncUserSource` callable's refusal: `HttpsError('failed-precondition', 'ai_credits_exhausted')`. */
export const isAiCreditsExhausted = (error: unknown) =>
  error instanceof FirebaseError &&
  error.code === 'functions/failed-precondition' &&
  aiCreditsExhaustedMessage.test(error.message);
